import React, { useEffect, useState, useMemo } from 'react';
import { Card, DatePicker, Table, Button, message, Space, Typography, Select } from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { dispatchStatsApi, DispatchTeamStat, DispatchStaffStat } from '../../services/system/dispatch-stats';
import { useTeamMap } from '../../services/store/organization.store';

const { Title, Text } = Typography;

const OrderDispatchStats: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [date, setDate] = useState<Dayjs>(dayjs());
  const [teamId, setTeamId] = useState<number | undefined>();
  const [stats, setStats] = useState<DispatchTeamStat[]>([]);
  const { teamList, teamLoading } = useTeamMap();

  const fetchStats = async () => {
    setLoading(true);
    try {
      const data = await dispatchStatsApi.getStats(
        date.format('YYYY-MM-DD'),
        teamId
      );
      setStats(data);
    } catch (error: any) {
      message.error(error?.message || '获取统计数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [date, teamId]);

  const handleTriggerTask = async () => {
    setTriggering(true);
    try {
      const res = await dispatchStatsApi.triggerTask('order_preprocess_syncer');
      message.success(`任务触发成功，总计 ${res.total} (派单: ${res.dispatch}, 取消: ${res.cancel}, 拦截: ${res.intercept})`);
      fetchStats();
    } catch (error: any) {
      message.error(error?.message || '任务触发失败');
    } finally {
      setTriggering(false);
    }
  };

  // 1. 各团队派单情况
  const teamColumns = [
    { title: '序号', dataIndex: 'index', width: 60, render: (_: any, __: any, idx: number) => idx + 1 },
    { title: '团队名称', dataIndex: 'teamName' },
    { title: '进件订单数', dataIndex: 'totalCreated' },
    { title: '已提交审核订单数', dataIndex: 'totalSubmitted' },
    {
      title: '已派单订单数',
      children: [
        { title: '合计', dataIndex: 'totalDispatched' },
        { title: 'A类订单', dataIndex: 'dispatchedA' },
        { title: 'B类订单', dataIndex: 'dispatchedB' },
        { title: 'C类订单', dataIndex: 'dispatchedC' },
        { title: 'D类订单', dataIndex: 'dispatchedD' },
        { title: '未知订单', dataIndex: 'dispatchedUnknown' },
      ],
    },
  ];

  const teamSummary = useMemo(() => {
    return stats.reduce((acc, curr) => ({
      totalCreated: acc.totalCreated + curr.totalCreated,
      totalSubmitted: acc.totalSubmitted + curr.totalSubmitted,
      totalDispatched: acc.totalDispatched + curr.totalDispatched,
      dispatchedA: acc.dispatchedA + curr.dispatchedA,
      dispatchedB: acc.dispatchedB + curr.dispatchedB,
      dispatchedC: acc.dispatchedC + curr.dispatchedC,
      dispatchedD: acc.dispatchedD + curr.dispatchedD,
      dispatchedUnknown: acc.dispatchedUnknown + curr.dispatchedUnknown,
    }), {
      totalCreated: 0,
      totalSubmitted: 0,
      totalDispatched: 0,
      dispatchedA: 0,
      dispatchedB: 0,
      dispatchedC: 0,
      dispatchedD: 0,
      dispatchedUnknown: 0,
    });
  }, [stats]);

  // 2. 团队内人员派单情况
  const staffColumns = [
    { title: '序号', dataIndex: 'index', width: 60, render: (_: any, __: any, idx: number) => idx + 1 },
    { title: '审核人', dataIndex: 'staffName' },
    {
      title: '已派单订单数',
      children: [
        { title: '合计', dataIndex: 'totalDispatched' },
        { title: 'A类订单', dataIndex: 'dispatchedA' },
        { title: 'B类订单', dataIndex: 'dispatchedB' },
        { title: 'C类订单', dataIndex: 'dispatchedC' },
        { title: 'D类订单', dataIndex: 'dispatchedD' },
        { title: '未知订单', dataIndex: 'dispatchedUnknown' },
      ],
    },
  ];

  const staffData = useMemo(() => {
    const list: DispatchStaffStat[] = [];
    stats.forEach(team => {
      team.staffStats.forEach(staff => {
        const exist = list.find(s => s.staffId === staff.staffId);
        if (exist) {
          exist.totalDispatched += staff.totalDispatched;
          exist.dispatchedA += staff.dispatchedA;
          exist.dispatchedB += staff.dispatchedB;
          exist.dispatchedC += staff.dispatchedC;
          exist.dispatchedD += staff.dispatchedD;
          exist.dispatchedUnknown += staff.dispatchedUnknown;
        } else {
          list.push({ ...staff });
        }
      });
    });
    return list;
  }, [stats]);

  return (
    <div style={{ padding: 24 }}>
      <Card
        title="订单派单统计"
        extra={
          <Button 
            type="primary" 
            icon={<SyncOutlined />} 
            onClick={handleTriggerTask}
            loading={triggering}
          >
            触发自动派单
          </Button>
        }
      >
        <Space style={{ marginBottom: 16 }}>
          <Text>筛选条件：进件时间</Text>
          <DatePicker 
            value={date} 
            onChange={(val) => val && setDate(val)} 
            allowClear={false}
          />
          <Text style={{ marginLeft: 24 }}>团队（仅对“人员派单情况”过滤）</Text>
          <Select
            allowClear
            placeholder="全部团队"
            style={{ width: 200 }}
            value={teamId}
            onChange={setTeamId}
            options={teamList.map(t => ({ label: t.name, value: t.id }))}
            loading={teamLoading}
          />
        </Space>

        <Title level={5} style={{ marginTop: 24 }}>各团队派单情况</Title>
        <Table
          columns={teamColumns}
          dataSource={stats}
          rowKey="teamId"
          pagination={false}
          loading={loading}
          bordered
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row style={{ background: '#fafafa', fontWeight: 'bold' }}>
                <Table.Summary.Cell index={0} colSpan={2}>合计</Table.Summary.Cell>
                <Table.Summary.Cell index={1}>{teamSummary.totalCreated}</Table.Summary.Cell>
                <Table.Summary.Cell index={2}>{teamSummary.totalSubmitted}</Table.Summary.Cell>
                <Table.Summary.Cell index={3}>{teamSummary.totalDispatched}</Table.Summary.Cell>
                <Table.Summary.Cell index={4}>{teamSummary.dispatchedA}</Table.Summary.Cell>
                <Table.Summary.Cell index={5}>{teamSummary.dispatchedB}</Table.Summary.Cell>
                <Table.Summary.Cell index={6}>{teamSummary.dispatchedC}</Table.Summary.Cell>
                <Table.Summary.Cell index={7}>{teamSummary.dispatchedD}</Table.Summary.Cell>
                <Table.Summary.Cell index={8}>{teamSummary.dispatchedUnknown}</Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />

        <Title level={5} style={{ marginTop: 40 }}>团队内人员派单情况</Title>
        <Table
          columns={staffColumns}
          dataSource={staffData}
          rowKey="staffId"
          pagination={false}
          loading={loading}
          bordered
          summary={() => {
            const staffSummary = staffData.reduce((acc, curr) => ({
              totalDispatched: acc.totalDispatched + curr.totalDispatched,
              dispatchedA: acc.dispatchedA + curr.dispatchedA,
              dispatchedB: acc.dispatchedB + curr.dispatchedB,
              dispatchedC: acc.dispatchedC + curr.dispatchedC,
              dispatchedD: acc.dispatchedD + curr.dispatchedD,
              dispatchedUnknown: acc.dispatchedUnknown + curr.dispatchedUnknown,
            }), {
              totalDispatched: 0, dispatchedA: 0, dispatchedB: 0, dispatchedC: 0, dispatchedD: 0, dispatchedUnknown: 0
            });
            return (
              <Table.Summary fixed>
                <Table.Summary.Row style={{ background: '#fafafa', fontWeight: 'bold' }}>
                  <Table.Summary.Cell index={0} colSpan={2}>合计</Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>{staffSummary.totalDispatched}</Table.Summary.Cell>
                  <Table.Summary.Cell index={2}>{staffSummary.dispatchedA}</Table.Summary.Cell>
                  <Table.Summary.Cell index={3}>{staffSummary.dispatchedB}</Table.Summary.Cell>
                  <Table.Summary.Cell index={4}>{staffSummary.dispatchedC}</Table.Summary.Cell>
                  <Table.Summary.Cell index={5}>{staffSummary.dispatchedD}</Table.Summary.Cell>
                  <Table.Summary.Cell index={6}>{staffSummary.dispatchedUnknown}</Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      </Card>
    </div>
  );
};

export default OrderDispatchStats;